// src/components/discovery/CreatorCard.jsx
//
// Extracted out of Discover.jsx, same reasoning as DropCard.jsx.

import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ProfileAvatarLink from "../profile/ProfileAvatarLink";
import ProfileNameLink from "../profile/ProfileNameLink";
import { getNicheMeta, NicheIcon } from "../niche";
import { formatCount } from "../../utils/formatCount";
import { thumbnailUrl } from "../../utils/discoveryPools";
import {
  DURATION,
  EASE,
  STAGGER_DELAY,
  STAGGER_CAP,
  tapPress,
  tapPressTransition,
  useMotionSafe,
} from "../../utils/motionTokens";

const NICHE_ACCENTS = {
  wellness: "linear-gradient(90deg,#C4790A,#d49010)",
  beauty: "linear-gradient(90deg,#6B4FA8,#a070e0)",
  food: "linear-gradient(90deg,#1A8C52,#30c870)",
  fitness: "linear-gradient(90deg,#D4520A,#f07030)",
  auto: "linear-gradient(90deg,#607080,#8090a0)",
  mind: "linear-gradient(90deg,#4A5280,#6a72a0)",
  arts: "linear-gradient(90deg,#9B3A3A,#b05050)",
  independent: "linear-gradient(90deg,#C4790A,#d49010)",
};

export default function CreatorCard({ creator, openDropCount, followingIds, isNew = false, isPaid = false, compact = false, index = 0 }) {
  const { prefersReduced } = useMotionSafe();
  const meta = getNicheMeta(creator.niche);
  const accent = NICHE_ACCENTS[meta.key] || NICHE_ACCENTS.independent;
  const handle = creator.handle || creator.username || creator.id;
  const isFollowing = followingIds.includes(creator.id);
  const totalDrops = creator.totalDrops || creator.dropCount || openDropCount || 0;
  const banner = creator.bannerImage || creator.bannerUrl || thumbnailUrl(null);

  const entranceDelay = Math.min(index, STAGGER_CAP) * STAGGER_DELAY;
  const entranceProps = prefersReduced
    ? {}
    : {
        initial: { opacity: 0, y: 14 },
        whileInView: {
          opacity: 1,
          y: 0,
          transition: { duration: DURATION.entrance, ease: EASE.out, delay: entranceDelay },
        },
        viewport: { once: true, amount: 0.3 },
      };

  return (
    <motion.article
      className={`gal-cc${compact ? " gal-cc--rail" : ""}`}
      {...entranceProps}
      whileTap={prefersReduced ? undefined : { ...tapPress, transition: tapPressTransition }}
    >
      <div className="gal-cc-banner">
        <img src={banner} alt="" />
        <div className="gal-cc-banner-shade" />
        <div className="gal-cc-accent" style={{ background: accent }} />
        {isNew && <span className="gal-cc-new-badge">New to 100VO</span>}
        {/* Same honesty principle as DropCard — neutral gray, never gold. */}
        {isPaid && <span className="gal-cc-paid-badge">Paid Spot</span>}
      </div>
      <div className="gal-cc-av-wrap">
        <ProfileAvatarLink
          user={creator}
          src={creator.avatarUrl || creator.avatar}
          alt={creator.displayName}
          className="gal-cc-av-link"
          imgClassName="gal-cc-av"
        />
      </div>
      <div className="gal-cc-body">
        <ProfileNameLink user={creator} className="gal-cc-name">
          {creator.displayName}
        </ProfileNameLink>
        <div className="gal-cc-niche">
          <NicheIcon niche={meta.key} size={10} />
          {meta.label}
          {creator.city ? ` · ${creator.city}` : ""}
        </div>
        {creator.bio && <p className="gal-cc-bio">{creator.bio.slice(0, 120)}</p>}
        {!isNew && (
          <div className="gal-cc-stats">
            <div className="gal-cc-stat">
              <strong>{formatCount(creator.followerCount || creator.supporterCount || 0)}</strong>
              <span>subscribers</span>
            </div>
            <div className="gal-cc-stat">
              <strong>{totalDrops}</strong>
              <span>drops</span>
            </div>
          </div>
        )}
        <div className="gal-cc-foot">
          <span className="gal-cc-drops">
            {openDropCount} drop{openDropCount === 1 ? "" : "s"} open
          </span>
          <Link
            to={`/creator/${handle}`}
            className={`gal-cc-follow${isFollowing ? " following" : ""}`}
          >
            {isFollowing ? "Following ✓" : "Follow"}
          </Link>
        </div>
      </div>
    </motion.article>
  );
}